import type {
  CloseType,
  LastPaymentPublic,
  NomineeRelationship,
  PolicyClose,
  PolicyClosurePublic,
  PolicyCreate,
  PolicyInterestPublic,
  PolicyLoanSummaryPublic,
  PolicyPublic,
  PolicyUpdate,
} from '../type/policy';
import type { CashPaymentType, ManagementPolicyPublic } from '../type/payment';
import type {
  LastPayment,
  Policy,
  PolicyClosure,
  PolicyInterestRow,
  PolicyLoanSummary,
} from '@/types';
import { toThreeDigits } from '@/lib/utils';
import { PLAN_MONTHS } from '@/lib/payments';

export const LastPaymentMapper = (payment?: LastPaymentPublic | null): LastPayment | null =>
  payment ? { date: payment.date, type: payment.type, amount: payment.amount } : null;

export const PolicyLoanSummaryMapper = (loan?: PolicyLoanSummaryPublic | null): PolicyLoanSummary => ({
  eligibility: loan?.eligibility ?? 'not_eligible',
  loanId: loan?.loan_id != null ? String(loan.loan_id) : undefined,
  loanNo: loan?.loan_no ?? undefined,
  amount: loan?.amount ?? undefined,
  principalPaid: loan?.principal_paid ?? undefined,
  outstanding: loan?.outstanding ?? undefined,
  thisMonth: loan?.this_month ?? undefined,
  lastPayment: LastPaymentMapper(loan?.last_payment),
  availableFrom: loan?.available_from ?? undefined,
  monthsNeeded: loan?.months_needed ?? undefined,
});

const PolicyClosureMapper = (closure?: PolicyClosurePublic | null): PolicyClosure | null =>
  closure
    ? {
        closedAt: closure.closed_at,
        closeType: closure.close_type,
        payoutType: closure.payout_type,
        principal: closure.principal,
        interest: closure.interest,
        balanceDeducted: closure.balance_deducted ?? 0,
        payoutAmount: closure.payout_amount,
        closureDocUrl: closure.closure_doc_url,
      }
    : null;

/** Full policy (GET /policies/{id}/). */
export const PolicyAPIResponseMapper = (policy: PolicyPublic): Policy => ({
  id: String(policy.id),
  memberId: String(policy.user_id),
  policyNo: policy.policy_no,
  cardNo: policy.card_no || undefined,
  status: policy.status,
  startMonth: policy.start_month,
  endMonth: policy.end_month,
  paidMonths: policy.paid_months,
  totalMonths: policy.total_months ?? PLAN_MONTHS,
  totalPaid: policy.total_paid,
  amountDue: policy.amount_due,
  nextDueMonth: policy.next_due_month ?? undefined,
  thisMonth: policy.this_month,
  lastPayment: LastPaymentMapper(policy.last_payment),
  nomineeName: policy.nominee_name,
  nomineeRelationship: policy.nominee_relationship,
  interestEarned: policy.savings?.interest_earned,
  totalValue: policy.savings?.total_value,
  loan: PolicyLoanSummaryMapper(policy.loan),
  memberBalanceDue: policy.member_balance_due ?? 0,
  closure: PolicyClosureMapper(policy.closure),
});

/** Policy row inside the payments management list. */
export const ManagementPolicyMapper = (memberId: number, policy: ManagementPolicyPublic): Policy => ({
  id: String(policy.policy_id),
  memberId: String(memberId),
  policyNo: policy.policy_no,
  cardNo: policy.card_no || undefined,
  status: policy.status,
  startMonth: policy.start_month,
  paidMonths: policy.paid_months,
  totalMonths: PLAN_MONTHS,
  totalPaid: policy.total_paid,
  amountDue: policy.amount_due,
  thisMonth: policy.this_month,
  lastPayment: LastPaymentMapper(policy.last_payment),
  loan: PolicyLoanSummaryMapper(policy.loan),
});

export const PolicyInterestMapper = (row: PolicyInterestPublic): PolicyInterestRow => ({
  month: row.month,
  policyPaid: row.policy_paid,
  interest: row.interest,
  totalInterest: row.total_interest,
  totalValue: row.total_value,
});

/** Card number as saved: digits padded to at least 3 (4 → '004'); empty → null. */
const toCardNo = (cardNo: string): string | null => {
  const value = cardNo.trim();
  return value ? toThreeDigits(value) : null;
};

export const PolicyAPIRequestMapper = (input: {
  memberId: string;
  startMonth: string;
  cardNo: string;
  nomineeName: string;
  nomineeRelationship: NomineeRelationship;
}): PolicyCreate => ({
  user_id: Number(input.memberId),
  start_month: input.startMonth,
  card_no: toCardNo(input.cardNo),
  nominee_name: input.nomineeName.trim(),
  nominee_relationship: input.nomineeRelationship,
});

/** Start month is only sent when it may still change (no payments yet). */
export const PolicyUpdateRequestMapper = (input: {
  cardNo: string;
  startMonth?: string;
  nomineeName: string;
  nomineeRelationship: NomineeRelationship;
}): PolicyUpdate => ({
  card_no: toCardNo(input.cardNo),
  ...(input.startMonth ? { start_month: input.startMonth } : {}),
  nominee_name: input.nomineeName.trim(),
  nominee_relationship: input.nomineeRelationship,
});

export const PolicyCloseRequestMapper = (input: {
  closeType: CloseType;
  payoutType: CashPaymentType;
  closureDocUrl: string;
}): PolicyClose => ({
  close_type: input.closeType,
  payout_type: input.payoutType,
  closure_doc_url: input.closureDocUrl,
});
